import { useContext, useEffect, useState, useMemo } from "react";
import { songLists } from "../assets/songs/songs.js";
import Playlist from "./Playlist";
import ExtendedSonglist from "./ExtendedPlaylist";
import { SongsContext } from "../context/SongsContext";
import CreateNewPlaylistButton from "./CreateNewPlaylistButton.js";
import {
    addDoc,
    collection,
    getDocs,
    serverTimestamp,
    updateDoc,
    doc,
    increment,
    deleteDoc,
} from "firebase/firestore";
import { db } from "../Firebase.js";
import CreateNewPlaylist from "./CreateNewPlaylist.js";
import { uuidv4 } from "@firebase/util";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import PlaylistInput from "./PlaylistInput";
import { FaArrowLeft, FaSearch, FaSort } from "react-icons/fa";
import SelectedSongs from "./SelectedSongs";
import "./SongPicker.mobile.css";

// Helper function to check if a string starts with a letter (including Norwegian characters)
const startsWithLetter = (str) => {
    if (!str || str.length === 0) return false;
    const firstChar = str.charAt(0);
    return /[a-zA-ZæøåÆØÅ]/.test(firstChar);
};

// Sort function for playlists
const sortPlaylists = (playlistsToSort, sortBy) => {
    const sorted = [...playlistsToSort];

    if (sortBy === "alphabetical") {
        sorted.sort((a, b) => {
            const aName = a.name || "";
            const bName = b.name || "";
            const aStartsWithLetter = startsWithLetter(aName);
            const bStartsWithLetter = startsWithLetter(bName);

            // If both start with letters, use normal alphabetical comparison
            if (aStartsWithLetter && bStartsWithLetter) {
                return aName.localeCompare(bName);
            }

            // If one starts with letter and other doesn't, letter comes first
            if (aStartsWithLetter && !bStartsWithLetter) return -1;
            if (!aStartsWithLetter && bStartsWithLetter) return 1;

            // If neither starts with letter, compare normally
            return aName.localeCompare(bName);
        });
    } else {
        // Sort by creation time (current implementation)
        sorted.sort((a, b) => {
            const aHasTimestamp = a.createdAt != null;
            const bHasTimestamp = b.createdAt != null;

            // If both have timestamps, sort by timestamp (newest first)
            if (aHasTimestamp && bHasTimestamp) {
                const timeA = a.createdAt?.toMillis
                    ? a.createdAt.toMillis()
                    : a.createdAt?.seconds * 1000 || 0;
                const timeB = b.createdAt?.toMillis
                    ? b.createdAt.toMillis()
                    : b.createdAt?.seconds * 1000 || 0;
                return timeB - timeA; // Descending order (newest first)
            }

            // If only one has timestamp, it comes first
            if (aHasTimestamp && !bHasTimestamp) return -1;
            if (!aHasTimestamp && bHasTimestamp) return 1;

            // If neither has timestamp, sort alphabetically by name
            const aName = a.name || "";
            const bName = b.name || "";
            const aStartsWithLetter = startsWithLetter(aName);
            const bStartsWithLetter = startsWithLetter(bName);

            // If both start with letters, use normal alphabetical comparison
            if (aStartsWithLetter && bStartsWithLetter) {
                return aName.localeCompare(bName);
            }

            // If one starts with letter and other doesn't, letter comes first
            if (aStartsWithLetter && !bStartsWithLetter) return -1;
            if (!aStartsWithLetter && bStartsWithLetter) return 1;

            // If neither starts with letter, compare normally
            return aName.localeCompare(bName);
        });
    }

    return sorted;
};

function SongPicker({ setMessage }) {
    // const [state, dispatch] = useContext(SongsContext);
    const [playlists, setPlaylists] = useState([]);
    const [selectedPlaylist, setSelectedPlaylist] = useState([]);
    const [allSongs, setAllSongs] = useState([]);
    const [isCreatingNewPlaylist, setIsCreatingNewPlaylist] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortType, setSortType] = useState("creationTime"); // "creationTime" or "alphabetical"
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [newSongs, setNewSongs] = useState([]);
    const [newPlaylistName, setNewPlaylistName] = useState("");
    const [newQuestions, setNewQuestions] = useState([]);
    const [enableQuestions, setEnableQuestions] = useState(false);
    const [editingPlaylist, setEditingPlaylist] = useState(null);
    const [godMode, setGodMode] = useState(false);
    const navigate = useNavigate();

    // Check if running on localhost
    const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

    // Enable god mode on localhost with keyboard shortcut (Ctrl+G or Cmd+G)
    useEffect(() => {
        if (!isLocalhost) return;

        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "g") {
                e.preventDefault();
                setGodMode((prev) => !prev);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isLocalhost]);

    const handleEditPlaylist = (playlist) => {
        setEditingPlaylist(playlist);
        setNewPlaylistName(playlist.name);
        setNewSongs(playlist.songs || []);
        setNewQuestions(playlist.questions || []);
        setEnableQuestions((playlist.questions || []).length > 0);
        setIsCreatingNewPlaylist(false);
        setSelectedPlaylist([]);
    };

    const handleSaveEdit = async () => {
        if (newSongs.length === 0) {
            alert("Du må velge minst én sang for å oppdatere spillelisten");
            return;
        }
        if (!editingPlaylist || !editingPlaylist.id) {
            alert("Feil: Ingen spilleliste valgt for redigering");
            return;
        }
        try {
            const updatedPlaylist = {
                name: newPlaylistName,
                songs: newSongs,
                questions:
                    enableQuestions && newQuestions.length > 0
                        ? newQuestions
                        : [],
            };
            const playlistRef = doc(db, "playlists", editingPlaylist.id);
            await updateDoc(playlistRef, updatedPlaylist);

            // Refresh playlists
            await getPlaylists();

            // Reset edit mode
            setEditingPlaylist(null);
            setNewPlaylistName("");
            setNewSongs([]);
            setNewQuestions([]);
            setEnableQuestions(false);
            setMessage("Spilleliste oppdatert!");
        } catch (error) {
            console.error("Error updating playlist:", error);
            alert("Feil ved oppdatering av spilleliste");
        }
    };

    const handleCancelEdit = () => {
        setEditingPlaylist(null);
        setNewPlaylistName("");
        setNewSongs([]);
        setNewQuestions([]);
        setEnableQuestions(false);
    };

    const handleDeletePlaylist = async (playlist) => {
        if (!playlist.id) {
            alert("Feil: Ingen spilleliste valgt for sletting");
            return;
        }

        const confirmDelete = window.confirm(
            `Er du sikker på at du vil slette spillelisten "${playlist.name}"? Denne handlingen kan ikke angres.`
        );

        if (!confirmDelete) {
            return;
        }

        try {
            // Delete playlist from Firestore
            const playlistRef = doc(db, "playlists", playlist.id);
            await deleteDoc(playlistRef);

            // Remove playlist ID from localStorage
            const myPlaylists = JSON.parse(
                localStorage.getItem("myPlaylists") || "[]"
            );
            const updatedPlaylists = myPlaylists.filter(
                (id) => id !== playlist.id
            );
            localStorage.setItem(
                "myPlaylists",
                JSON.stringify(updatedPlaylists)
            );

            // Clear selection if this playlist was selected
            if (selectedPlaylist?.id === playlist.id) {
                setSelectedPlaylist([]);
            }

            // Refresh playlists
            await getPlaylists();
            setMessage("Spilleliste slettet!");
        } catch (error) {
            console.error("Error deleting playlist:", error);
            alert("Feil ved sletting av spilleliste");
        }
    };

    const handleAddToMyPlaylists = (playlist) => {
        if (!playlist.id) {
            alert("Feil: Ingen spilleliste valgt");
            return;
        }

        const myPlaylists = JSON.parse(
            localStorage.getItem("myPlaylists") || "[]"
        );
        if (!myPlaylists.includes(playlist.id)) {
            myPlaylists.push(playlist.id);
            localStorage.setItem("myPlaylists", JSON.stringify(myPlaylists));
            setMessage(`"${playlist.name}" lagt til i mine spillelister!`);
        } else {
            setMessage(`"${playlist.name}" er allerede i dine spillelister`);
        }
    };

    useEffect(() => {
        getPlaylists();
        getSongs();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showSortDropdown &&
                !event.target.closest("[data-sort-dropdown]")
            ) {
                setShowSortDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSortDropdown]);

    useEffect(() => {
        // Clear search when creating new playlist
        if (isCreatingNewPlaylist) {
            setSearchTerm("");
        } else {
            // Clear new songs when exiting create mode
            setNewSongs([]);
            setNewPlaylistName("");
            setNewQuestions([]);
            setEnableQuestions(false);
        }
    }, [isCreatingNewPlaylist]);

    const addNewPlaylist = async () => {
        if (newPlaylistName === "") {
            alert("Du må velge et navn på spillelisten");
            return;
        }
        if (newSongs.length === 0) {
            alert("Du må velge minst én sang for å opprette en spilleliste");
            return;
        }
        try {
            const newPlaylist = {
                name: newPlaylistName,
                songs: newSongs,
                questions:
                    enableQuestions && newQuestions.length > 0
                        ? newQuestions
                        : [],
                createdAt: serverTimestamp(),
            };
            const docRef = await addDoc(
                collection(db, "playlists"),
                newPlaylist
            );

            // Store playlist ID in localStorage
            const myPlaylists = JSON.parse(
                localStorage.getItem("myPlaylists") || "[]"
            );
            if (!myPlaylists.includes(docRef.id)) {
                myPlaylists.push(docRef.id);
                localStorage.setItem(
                    "myPlaylists",
                    JSON.stringify(myPlaylists)
                );
            }

            // Update usageCount for each song in the playlist
            try {
                // Get all songs to find their document IDs
                const songsSnapshot = await getDocs(collection(db, "songs"));
                const songIdToDocId = new Map();

                songsSnapshot.forEach((doc) => {
                    const songData = doc.data();
                    if (songData.songID) {
                        songIdToDocId.set(songData.songID, doc.id);
                    }
                });

                // Deduplicate songs by songID to avoid counting the same song multiple times
                const uniqueSongIDs = new Set();
                newSongs.forEach((song) => {
                    if (song.songID) {
                        uniqueSongIDs.add(song.songID);
                    }
                });

                // Increment usageCount for each unique song in the new playlist
                const updatePromises = Array.from(uniqueSongIDs).map(
                    async (songID) => {
                        const docId = songIdToDocId.get(songID);
                        if (docId) {
                            try {
                                const songRef = doc(db, "songs", docId);
                                await updateDoc(songRef, {
                                    usageCount: increment(1),
                                });
                            } catch (error) {
                                console.error(
                                    `Error updating usageCount for song ${songID}:`,
                                    error
                                );
                            }
                        } else {
                            console.warn(
                                `Could not find document ID for songID: ${songID}`
                            );
                        }
                    }
                );

                await Promise.all(updatePromises);
            } catch (error) {
                console.error("Error updating song usage counts:", error);
                // Don't fail the playlist creation if usage count update fails
            }

            setNewSongs([]);
            setNewPlaylistName("");
            // Refetch songs to get updated usageCounts
            await getSongs();
            // Refetch playlists to get sorted list with new playlist
            const refetchedPlaylists = await getPlaylists();
            // Find the newly created playlist from refetched list and set it as selected
            const createdPlaylist = refetchedPlaylists.find(
                (p) => p.name === newPlaylistName
            );
            if (createdPlaylist) {
                setSelectedPlaylist(createdPlaylist);
            } else {
                setSelectedPlaylist(newPlaylist);
            }
            setIsCreatingNewPlaylist(false);
        } catch (e) {
            console.error("Error adding document: ", e);
            alert(
                "Det oppstod en feil ved lagring av spillelisten. Vennligst prøv igjen."
            );
        }
    };

    // Memoize filtered and sorted playlists
    const filteredPlaylists = useMemo(() => {
        let filtered = playlists;

        // Apply search filter
        if (searchTerm && searchTerm.trim().length > 0) {
            const search = searchTerm.trim().toLowerCase();
            filtered = playlists.filter((playlist) => {
                const playlistName = (playlist.name || "").toLowerCase();
                return playlistName.includes(search);
            });
        }

        // Apply sorting
        return sortPlaylists(filtered, sortType);
    }, [playlists, searchTerm, sortType]);

    const getPlaylists = async () => {
        const querySnapshot = await getDocs(collection(db, "playlists"));
        let newPlaylists = [];
        querySnapshot.forEach((doc) => {
            let data = doc.data();
            // Include document ID for reference
            data.id = doc.id;
            newPlaylists.push(data);
        });

        // Don't sort here - sorting is handled in filteredPlaylists useMemo
        setPlaylists(newPlaylists);
        return newPlaylists;
    };

    const getSongs = async () => {
        const querySnapshot = await getDocs(collection(db, "songs"));
        let newAllSongs = [];
        querySnapshot.forEach((doc) => {
            let data = doc.data();
            newAllSongs.push(data);
        });
        setAllSongs(newAllSongs);
    };

    // Function to recount usageCount for all songs based on all playlists
    const recountAllSongUsage = async () => {
        try {
            console.log("Starting to recount song usage...");

            // Get all playlists
            const playlistsSnapshot = await getDocs(
                collection(db, "playlists")
            );
            const songUsageMap = new Map(); // songID -> count of playlists containing it

            // Count how many playlists each song appears in
            playlistsSnapshot.forEach((playlistDoc) => {
                const playlistData = playlistDoc.data();
                const songs = playlistData.songs || [];

                // Use Set to deduplicate songs within this playlist
                const uniqueSongIDs = new Set();
                songs.forEach((song) => {
                    if (song.songID) {
                        uniqueSongIDs.add(song.songID);
                    }
                });

                // Increment count for each unique song in this playlist
                uniqueSongIDs.forEach((songID) => {
                    songUsageMap.set(
                        songID,
                        (songUsageMap.get(songID) || 0) + 1
                    );
                });
            });

            console.log(
                `Found ${songUsageMap.size} unique songs across all playlists`
            );

            // Debug: Log some example songIDs from the map
            const exampleSongIDs = Array.from(songUsageMap.entries()).slice(
                0,
                5
            );
            console.log("Example songIDs in map:", exampleSongIDs);

            // Get all songs to update their usageCount
            const songsSnapshot = await getDocs(collection(db, "songs"));
            console.log(`Found ${songsSnapshot.size} songs in database`);
            const updatePromises = [];
            let songsWithoutSongID = 0;
            let songsNotFoundInMap = 0;

            songsSnapshot.forEach((songDoc) => {
                const songData = songDoc.data();
                const songID = songData.songID;

                if (!songID) {
                    songsWithoutSongID++;
                    console.warn(
                        `Song without songID: "${songData.title}" (doc ID: ${songDoc.id})`
                    );
                    return;
                }

                const actualCount = songUsageMap.get(songID) || 0;
                const currentCount = songData.usageCount || 0;

                if (actualCount === 0 && songUsageMap.size > 0) {
                    songsNotFoundInMap++;
                    console.warn(
                        `Song "${songData.title}" (songID: ${songID}) not found in any playlist`
                    );
                }

                // Only update if the count is different
                if (actualCount !== currentCount) {
                    console.log(
                        `Updating "${songData.title}": ${currentCount} -> ${actualCount} (songID: ${songID})`
                    );
                    const songRef = doc(db, "songs", songDoc.id);
                    updatePromises.push(
                        updateDoc(songRef, {
                            usageCount: actualCount,
                        })
                    );
                }
            });

            console.log(`Songs without songID: ${songsWithoutSongID}`);
            console.log(`Songs not found in playlists: ${songsNotFoundInMap}`);

            await Promise.all(updatePromises);
            console.log(`Updated ${updatePromises.length} songs`);

            // Refetch songs to get updated counts
            await getSongs();

            alert(
                `Rekalkulert bruk for alle sanger. ${updatePromises.length} sanger ble oppdatert.`
            );
        } catch (error) {
            console.error("Error recounting song usage:", error);
            alert(
                "Feil ved rekalkulering av sangbruk. Se konsollen for detaljer."
            );
        }
    };

    const createBeatForBeatSession = async () => {
        try {
            // Build gameItems: interleave songs and questions based on question.order
            const songs = selectedPlaylist.songs || [];
            const questions = selectedPlaylist.questions || [];

            const gameItems = [];

            // Add all songs as game items first
            songs.forEach((song) => {
                gameItems.push({ type: "song", data: song });
            });

            // Insert questions at their correct positions (order = after which song index)
            // Sort by order descending so we can insert without shifting issues
            const sortedQuestions = [...questions]
                .filter((q) => q.order !== undefined)
                .sort((a, b) => b.order - a.order);

            sortedQuestions.forEach((question) => {
                const insertPosition = Math.min(
                    question.order + 1,
                    gameItems.length
                );
                gameItems.splice(insertPosition, 0, {
                    type: "question",
                    data: question,
                });
            });

            // Append questions without an order at the end
            questions
                .filter((q) => q.order === undefined)
                .forEach((question) => {
                    gameItems.push({ type: "question", data: question });
                });

            const newSession = {
                songList: selectedPlaylist,
                gameItems: gameItems,
                maxScore: 20,
                team1: {
                    score: 0,
                    usedPhone: false,
                    name: "Team Nagell",
                },
                team2: {
                    score: 0,
                    usedPhone: false,
                    name: "Team Styve",
                },
                currentIndex: 0,
                isGameActive: true,
                playedSongs: [],
                showCountdown: false,
                redBoxes: [],
                showBoxes: [],
            };
            const docRef = await addDoc(collection(db, "sessions"), newSession);
            console.log("Session startet with document ID: ", docRef.id);
            setSessionId(docRef.id);
            //Generate QR code to scan admin panel on mobile
        } catch (e) {
            console.error("Error starting session: ", e);
        }
    };

    return (
        <div
            className="song-picker-container"
            style={{
                width: "100vw",
                height: "100vh",
                backgroundColor: "#161f33",
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
                position: "relative",
            }}
        >
            <div
                className="song-picker-sidebar"
                style={{
                    flexDirection: "column",
                    display: "flex",
                    width: "25%",
                    minWidth: "25%",
                    maxWidth: "25%",
                    backgroundColor: "#0f1624",
                    borderRight: "1px solid #2a3854",
                    overflow: "hidden",
                }}
            >
                {isCreatingNewPlaylist || editingPlaylist ? (
                    <div
                        className="create-playlist-wrapper"
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                padding: "2vh 1vh 1vh 1vh",
                                borderBottom: "1px solid #2a3854",
                            }}
                        >
                            <div
                                onClick={() => {
                                    if (editingPlaylist) {
                                        handleCancelEdit();
                                    } else {
                                        setIsCreatingNewPlaylist(false);
                                    }
                                }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    color: "white",
                                    marginRight: "1vh",
                                    padding: "0.5vh",
                                }}
                            >
                                <FaArrowLeft />
                            </div>
                            <h3
                                style={{
                                    color: "white",
                                    textAlign: "left",
                                    margin: 0,
                                    fontSize: 18,
                                    fontWeight: 600,
                                }}
                            >
                                {editingPlaylist
                                    ? "Rediger spilleliste"
                                    : "Ny spilleliste"}
                            </h3>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                overflow: "hidden",
                                minHeight: 0,
                            }}
                        >
                            <CreateNewPlaylist
                                allSongs={allSongs}
                                newSongs={newSongs}
                                setNewSongs={setNewSongs}
                                newPlaylistName={newPlaylistName}
                                setNewPlaylistName={setNewPlaylistName}
                                newQuestions={newQuestions}
                                setNewQuestions={setNewQuestions}
                                enableQuestions={enableQuestions}
                                setEnableQuestions={setEnableQuestions}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                flexShrink: 0,
                                marginBottom: "1vh",
                                paddingLeft: "1vh",
                            }}
                        >
                            <h3
                                style={{
                                    color: "white",
                                    textAlign: "left",
                                    marginLeft: "1vh",
                                    marginTop: "2vh",
                                    marginBottom: "0.5vh",
                                    fontSize: 16,
                                }}
                            >
                                Velg blant {playlists.length} spillelister
                            </h3>
                            {/* <div
                                style={{
                                    color: "#9ca3af",
                                    textAlign: "left",
                                    marginLeft: "1vh",
                                    marginBottom: "1vh",
                                    fontSize: 12,
                                }}
                            >
                                {playlists.length} spillelister
                            </div> */}
                            <div
                                style={{
                                    display: "flex",
                                    gap: "1vh",
                                    width: "90%",
                                    margin: "1vh 1vh 0 1vh",
                                }}
                            >
                                <div
                                    style={{
                                        position: "relative",
                                        flex: 1,
                                    }}
                                >
                                    <FaSearch
                                        style={{
                                            position: "absolute",
                                            left: "1.5vh",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "#9ca3af",
                                            fontSize: 12,
                                            pointerEvents: "none",
                                            zIndex: 1,
                                        }}
                                    />
                                    <PlaylistInput
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                        placeholder="Søk spillelister..."
                                        extraStyle={{
                                            width: "100%",
                                            height: "3vh",
                                            margin: 0,
                                            borderRadius: "0.5vh",
                                            padding: "3px 1%",
                                            paddingLeft: "3.5vh",
                                            fontSize: 14,
                                            boxSizing: "border-box",
                                            backgroundColor: "#2a3854",
                                            border: "1px solid #2a3854",
                                            color: "white",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        position: "relative",
                                    }}
                                    data-sort-dropdown
                                >
                                    <button
                                        onClick={() =>
                                            setShowSortDropdown(
                                                !showSortDropdown
                                            )
                                        }
                                        style={{
                                            height: "3vh",
                                            minWidth: "3vh",
                                            padding: "0 1vh",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "0.5vh",
                                            backgroundColor: "#2a3854",
                                            border: "1px solid #2a3854",
                                            borderRadius: "0.5vh",
                                            color: "white",
                                            cursor: "pointer",
                                            fontSize: 14,
                                            transition: "background-color 0.2s",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                                "#3a4a64";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                                "#2a3854";
                                        }}
                                    >
                                        <FaSort style={{ fontSize: 12 }} />
                                    </button>
                                    {showSortDropdown && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "100%",
                                                right: 0,
                                                marginTop: "0.5vh",
                                                backgroundColor: "#2a3854",
                                                border: "1px solid #3a4a64",
                                                borderRadius: "0.5vh",
                                                minWidth: "180px",
                                                zIndex: 1000,
                                                boxShadow:
                                                    "0 4px 6px rgba(0, 0, 0, 0.3)",
                                            }}
                                        >
                                            <button
                                                onClick={() => {
                                                    setSortType("creationTime");
                                                    setShowSortDropdown(false);
                                                }}
                                                style={{
                                                    width: "100%",
                                                    padding: "1vh",
                                                    textAlign: "left",
                                                    backgroundColor:
                                                        sortType ===
                                                        "creationTime"
                                                            ? "#3a4a64"
                                                            : "transparent",
                                                    border: "none",
                                                    color: "white",
                                                    cursor: "pointer",
                                                    fontSize: 14,
                                                    borderTopLeftRadius:
                                                        "0.5vh",
                                                    borderTopRightRadius:
                                                        "0.5vh",
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (
                                                        sortType !==
                                                        "creationTime"
                                                    ) {
                                                        e.currentTarget.style.backgroundColor =
                                                            "#3a4a64";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (
                                                        sortType !==
                                                        "creationTime"
                                                    ) {
                                                        e.currentTarget.style.backgroundColor =
                                                            "transparent";
                                                    }
                                                }}
                                            >
                                                Nyeste
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortType("alphabetical");
                                                    setShowSortDropdown(false);
                                                }}
                                                style={{
                                                    width: "100%",
                                                    padding: "1vh",
                                                    textAlign: "left",
                                                    backgroundColor:
                                                        sortType ===
                                                        "alphabetical"
                                                            ? "#3a4a64"
                                                            : "transparent",
                                                    border: "none",
                                                    color: "white",
                                                    cursor: "pointer",
                                                    fontSize: 14,
                                                    borderBottomLeftRadius:
                                                        "0.5vh",
                                                    borderBottomRightRadius:
                                                        "0.5vh",
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (
                                                        sortType !==
                                                        "alphabetical"
                                                    ) {
                                                        e.currentTarget.style.backgroundColor =
                                                            "#3a4a64";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (
                                                        sortType !==
                                                        "alphabetical"
                                                    ) {
                                                        e.currentTarget.style.backgroundColor =
                                                            "transparent";
                                                    }
                                                }}
                                            >
                                                Alfabetisk
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                scrollbarWidth: "thin",
                                scrollbarColor: "#4db193 #141b2d",
                                backgroundColor: "#141b2d",
                                minHeight: 0,
                                paddingTop: "1vh",
                                paddingLeft: "1vh",
                                paddingBottom: "2vh",
                                borderTop: "1px solid #2a3854",
                            }}
                        >
                            {filteredPlaylists.map((playlist, index) => {
                                return (
                                    <Playlist
                                        key={
                                            // playlist.id ||
                                            playlist.name + index
                                            // `playlist-${index} + ${playlist.name} + ${playlist.id}`
                                        }
                                        songList={playlist}
                                        setMessage={setMessage}
                                        setSelectedPlaylist={
                                            setSelectedPlaylist
                                        }
                                        selectedPlaylist={selectedPlaylist}
                                        setIsCreatingNewPlaylist={
                                            setIsCreatingNewPlaylist
                                        }
                                        onEdit={handleEditPlaylist}
                                        onDelete={handleDeletePlaylist}
                                        godMode={godMode}
                                        onAddToMyPlaylists={
                                            godMode
                                                ? handleAddToMyPlaylists
                                                : null
                                        }
                                    />
                                );
                            })}
                            {/*<AddSong />*/}
                        </div>
                        <div
                            style={{
                                flexShrink: 0,
                                padding: "2vh 1vh 4vh 1vh",
                                borderTop: "1px solid #2a3854",
                                backgroundColor: "#0f1624",
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    paddingLeft: "1vh",
                                    marginBottom: "0.5vh",
                                    textAlign: "left",
                                    color: "white",
                                    fontSize: 16,
                                    fontWeight: 600,
                                }}
                            >
                                Eller
                            </div>
                            <CreateNewPlaylistButton
                                // key="newPlayList"
                                setIsCreatingNewPlaylist={
                                    setIsCreatingNewPlaylist
                                }
                                isCreatingNewPlaylist={isCreatingNewPlaylist}
                                setSelectedPlaylist={setSelectedPlaylist}
                            />
                            {isLocalhost && (
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: godMode ? "#fbbf24" : "#9ca3af",
                                        padding: "0.5vh 1vh",
                                        borderRadius: "0.3vh",
                                        backgroundColor: godMode
                                            ? "#3a2f1a"
                                            : "transparent",
                                        border: godMode
                                            ? "1px solid #fbbf24"
                                            : "none",
                                        cursor: "pointer",
                                        userSelect: "none",
                                    }}
                                    onClick={() => setGodMode(!godMode)}
                                    title="Press Ctrl+G (Cmd+G on Mac) to toggle"
                                >
                                    {godMode ? "🔓 GOD MODE" : "🔒 Locked"}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
            <div
                className="song-picker-content"
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    paddingRight: 20,
                    paddingBottom: 20,
                }}
            >
                <h2
                    style={{
                        color: "white",
                        margin: 0,
                        padding: "20px 0 10px 0",
                        flexShrink: 0,
                    }}
                >
                    {isCreatingNewPlaylist
                        ? "Ny spilleliste"
                        : editingPlaylist
                        ? "Rediger spilleliste"
                        : "Velg spilleliste"}
                </h2>
                {!isCreatingNewPlaylist &&
                    !editingPlaylist &&
                    !selectedPlaylist?.name && (
                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "4vh 5vw",
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#1f2937",
                                    border: "2px dashed #fbbf24",
                                    borderRadius: "1vh",
                                    padding: "4vh 3vw",
                                    maxWidth: "600px",
                                    width: "100%",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "2rem",
                                        marginBottom: "2vh",
                                    }}
                                >
                                    ⚠️
                                </div>
                                <div
                                    style={{
                                        fontSize: "1.5rem",
                                        color: "#fbbf24",
                                        fontWeight: "bold",
                                        marginBottom: "1.5vh",
                                    }}
                                >
                                    Viktig!
                                </div>
                                <div
                                    style={{
                                        fontSize: "1.1rem",
                                        color: "#d1d5db",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Skjul skjermen for deltagerne før du velger
                                    en spilleliste og starter spillet.
                                </div>
                                <div
                                    style={{
                                        fontSize: "0.95rem",
                                        color: "#9ca3af",
                                        marginTop: "2vh",
                                        fontStyle: "italic",
                                    }}
                                >
                                    Dette spillet krever en fasilitator som
                                    styrer maskinen og er dommer.
                                </div>
                            </div>
                        </div>
                    )}
                {(isCreatingNewPlaylist || editingPlaylist) && (
                    <div
                        style={{
                            flexDirection: "column",
                            display: "flex",
                            width: "100%",
                            flex: 1,
                            overflow: "hidden",
                            paddingTop: "1vh",
                        }}
                    >
                        <SelectedSongs
                            addNewPlaylist={addNewPlaylist}
                            newSongs={newSongs}
                            setNewSongs={setNewSongs}
                            newQuestions={newQuestions}
                            setNewQuestions={setNewQuestions}
                            enableQuestions={enableQuestions}
                            isEditMode={!!editingPlaylist}
                            onSaveEdit={handleSaveEdit}
                            onCancelEdit={handleCancelEdit}
                        />
                    </div>
                )}
                {!isCreatingNewPlaylist && selectedPlaylist?.name && (
                    <div
                        style={{
                            flexDirection: "row",
                            display: "flex",
                            width: "100%",
                            flex: 1,
                            flexWrap: "wrap",
                            justifyContent: "center",
                            overflow: "hidden",
                            minHeight: 0,
                        }}
                    >
                        <ExtendedSonglist songList={selectedPlaylist} />
                    </div>
                )}
                {selectedPlaylist.name && !sessionId ? (
                    <div
                        key="startSession"
                        onClick={createBeatForBeatSession}
                        style={{
                            height: "4vh",
                            width: "20%",
                            backgroundColor: "#4db193",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 5,
                            cursor: "pointer",
                            padding: 5,
                            marginTop: 10,
                            fontSize: 18,
                            alignSelf: "center",
                            position: "absolute",
                            right: 20,
                            bottom: 20,
                        }}
                    >
                        Start økt
                    </div>
                ) : null}

                {sessionId ? (
                    <div
                        style={{
                            position: "absolute",
                            right: 10,
                            bottom: 100,
                            width: "15vw",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                padding: 10,
                                color: "white",
                                textAlign: "center",
                                marginBottom: 10,
                            }}
                        >
                            Scan QR-koden med mobilen og styr spillet derfra ✌️
                        </div>
                        <div
                            style={{
                                padding: 10,
                                backgroundColor: "white",
                                width: 100,
                                height: 100,
                            }}
                        >
                            <QRCode
                                size={100}
                                value={
                                    "https://b4b.gameguru.no/control/" +
                                    sessionId
                                }
                            />
                        </div>
                    </div>
                ) : null}

                {sessionId ? (
                    <div
                        key="goToGame"
                        onClick={() =>
                            navigate("../game/" + sessionId, { replace: true })
                        }
                        style={{
                            height: "4vh",
                            width: "20%",
                            backgroundColor: "#4db193",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 5,
                            cursor: "pointer",
                            padding: 5,
                            marginTop: 10,
                            fontSize: 18,
                            alignSelf: "center",
                            position: "absolute",
                            right: 20,
                            bottom: 20,
                        }}
                    >
                        Gå til økt
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default SongPicker;
